import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import { PASSWORD_RULES, isPasswordValid } from '../../utils/passwordValidation.js';
import { EMAIL_FORMAT_ERROR_MESSAGE, isEmailFormatAcceptable } from '../../utils/emailValidation.js';
import TruncatedCell from '../../components/TruncatedCell.js';
import { useAuth } from '../../contexts/AuthContext.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';
import Pagination from '../../components/Pagination.js';
import usePaginatedFetch from '../../utils/usePaginatedFetch.js';
import { formatToJSTDate } from '../../utils/dateFormatter.js';

function UserArchivePage() {
    const [restoreModal, setRestoreModal] = useState({ open: false, user: null });
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const fixedParams = useMemo(() => ({}), []);
    const paged = usePaginatedFetch('/users/archived', fixedParams, 20);

    useEffect(() => {
        if (currentUser && !currentUser.is_admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        paged.fetch();
    }, [paged.page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (paged.forbidden) navigate('/');
    }, [paged.forbidden, navigate]);

    return (
        <div className="user-management-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 className="page-title">削除済みユーザー</h1>
                <button
                    className="btn-toolbar"
                    onClick={() => navigate('/settings/users')}
                >
                    ← ユーザー管理に戻る
                </button>
            </div>

            <div className="meeting-list-card">
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>
                    削除されたユーザーは元のメールアドレスを復元できません。復元時には新しいメールアドレスとパスワードを設定してください。
                </p>
                {paged.loading && paged.items.length === 0 && <SkeletonLoader variant="list" rows={5} />}
                {paged.error && <p className="error-message">エラー: {paged.error}</p>}
                {(!paged.loading || paged.items.length > 0) && !paged.error && (
                    <div className="table-responsive" style={{
                        opacity: paged.loading ? 0.6 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: paged.loading ? 'none' : 'auto'
                    }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>氏名</th>
                                    <th>権限</th>
                                    <th>AI利用</th>
                                    <th>登録日</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.items.length > 0 ? (
                                    paged.items.map(user => (
                                        <tr key={user.id}>
                                            <td><TruncatedCell text={user.user_name} /></td>
                                            <td>
                                                <span
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: user.is_admin ? '#ede7f6' : '#f5f5f5',
                                                        color: user.is_admin ? '#512da8' : '#616161',
                                                    }}
                                                >
                                                    {user.is_admin ? '管理者' : '一般'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: user.has_ai_access ? '#e8f5e9' : '#ffebee',
                                                        color: user.has_ai_access ? '#2e7d32' : '#c62828',
                                                    }}
                                                >
                                                    {user.has_ai_access ? '利用可' : '利用不可'}
                                                </span>
                                            </td>
                                            <td style={{ color: '#666', fontSize: '13px' }}>
                                                {formatToJSTDate(user.created_at)}
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => setRestoreModal({ open: true, user })}
                                                    className="btn-row btn-row--primary"
                                                >
                                                    ↩ 復元
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="no-data">削除済みユーザーはありません。</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!paged.loading && !paged.error && paged.totalPages > 0 && (
                    <Pagination
                        currentPage={paged.page}
                        totalPages={paged.totalPages}
                        totalItems={paged.total}
                        perPage={paged.perPage}
                        onPageChange={paged.setPage}
                    />
                )}
            </div>

            {restoreModal.open && (
                <RestoreUserModal
                    user={restoreModal.user}
                    onClose={() => setRestoreModal({ open: false, user: null })}
                    onSaved={() => {
                        setRestoreModal({ open: false, user: null });
                        paged.fetch();
                    }}
                />
            )}
        </div>
    );
}

function RestoreUserModal({ user, onClose, onSaved }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [showPwdConfirm, setShowPwdConfirm] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const passwordMatch = password && password === passwordConfirm;
    const passwordValid = isPasswordValid(password);
    const canSubmit = email && passwordValid && passwordMatch;

    const handleSubmit = async (e) => {
        e.preventDefault();
        // ブラウザの type="email" は a@b を通すが、サーバは 422（英語の検査の文言）を返すため、送る前に止める
        if (!isEmailFormatAcceptable(email)) {
            setError(EMAIL_FORMAT_ERROR_MESSAGE);
            return;
        }
        if (!passwordMatch) {
            setError('パスワードが一致しません。');
            return;
        }
        if (!passwordValid) {
            setError('パスワードは8文字以上で、英字と数字をそれぞれ1文字以上含めてください。');
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const response = await api.fetch(`/users/${user.id}/restore`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_email: email, password }),
            });
            if (response && response.ok) {
                onSaved();
            } else {
                const msg = await handleApiError(response, 'ユーザーの復元に失敗しました。');
                setError(msg);
            }
        } catch (err) {
            setError('通信エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h3 className="modal-title">ユーザー復元</h3>
                <p style={{ marginBottom: '15px' }}>
                    {user?.user_name} 様を復元します。新しいメールアドレスとパスワードを設定してください。
                </p>
                <form onSubmit={handleSubmit}>
                    <div className="auth-form-group">
                        <label className="auth-label">メールアドレス *</label>
                        <input
                            type="email"
                            className="auth-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="user@example.com"
                        />
                    </div>
                    <div className="auth-form-group">
                        <label className="auth-label">パスワード *</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPwd ? 'text' : 'password'}
                                className="auth-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="新しいパスワード"
                                style={{ paddingRight: '40px' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd(!showPwd)}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    fontSize: '12px',
                                    color: '#666',
                                }}
                            >
                                {showPwd ? '隠す' : '表示'}
                            </button>
                        </div>
                        {password && (
                            <div className="password-rules">
                                {PASSWORD_RULES.map((rule, i) => (
                                    <small key={i} className={rule.test(password) ? 'rule-pass' : 'rule-fail'}>
                                        {rule.test(password) ? '✓' : '✗'} {rule.label}
                                    </small>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="auth-form-group">
                        <label className="auth-label">パスワード（確認用） *</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPwdConfirm ? 'text' : 'password'}
                                className="auth-input"
                                value={passwordConfirm}
                                onChange={(e) => setPasswordConfirm(e.target.value)}
                                required
                                placeholder="パスワード（確認用）"
                                style={{ paddingRight: '40px' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwdConfirm(!showPwdConfirm)}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    fontSize: '12px',
                                    color: '#666',
                                }}
                            >
                                {showPwdConfirm ? '隠す' : '表示'}
                            </button>
                        </div>
                        {passwordConfirm && !passwordMatch && (
                            <small style={{ color: '#c62828' }}>パスワードが一致しません</small>
                        )}
                    </div>
                    {error && <div className="auth-error">{error}</div>}
                    <div className="modal-actions">
                        <button type="button" className="modal-button-cancel" onClick={onClose} disabled={loading}>
                            キャンセル
                        </button>
                        <button type="submit" className="modal-button-confirm" disabled={loading || !canSubmit}>
                            {loading ? '処理中...' : '復元する'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default UserArchivePage;
